import { useEffect, useMemo, useRef } from 'react';
import { getLatestWorkpack, isWorkpackExpired } from './utils/workpackUtils';
import { POLLING_BACKEND_STATUSES } from './utils/workpackStatusConstants';

export const config = {
    schedule: [
        {
            delay: 20 * 1000, // 20 seconds
            repeat: 6
        },
        {
            delay: 60 * 1000, // 1 minute
            repeat: 6
        },
        {
            delay: 2 * 60 * 1000, // 2 minutes
            repeat: 6
        },
        {
            delay: 5 * 60 * 1000, // 5 minutes
            repeat: 2
        }
    ]
};

const POLL_SCHEDULE_MS = config.schedule.flatMap(({ delay, repeat }) => Array(repeat).fill(delay));

const CUTOFF = POLL_SCHEDULE_MS.reduce((acc, val) => acc + val, 0) + 1000; // add 1 second buffer

export const usePollWorkpacks = ({ workpackRequests, revalidate }) => {
    const timeoutRef = useRef(null);
    const missedWhileHiddenRef = useRef(false);
    const catchUpPollDoneRef = useRef(false);
    const pollIndexRef = useRef(0);
    const pollStartRef = useRef(0);

    const latestWorkpacks = useMemo(() => {
        const requests = workpackRequests?.content || [];
        return requests.reduce((acc, request) => {
            const latest = getLatestWorkpack(request?.workpacks);
            if (latest?.status) {
                acc.push(latest);
            } else if (!isWorkpackExpired(request?.dateCreated)) {
                // no workpack sub-record yet (e.g. backend read model hasn't caught up with a
                // just-created request) - keep polling until one appears or the request expires
                acc.push({ status: null });
            }
            return acc;
        }, []);
    }, [workpackRequests]);

    const needsPolling = useMemo(() => {
        return latestWorkpacks.some(workpack => workpack.status === null || POLLING_BACKEND_STATUSES.has(workpack.status));
    }, [latestWorkpacks]);

    useEffect(() => {
        if (!needsPolling) {
            return;
        }

        pollIndexRef.current = 0;
        pollStartRef.current = Date.now();
        missedWhileHiddenRef.current = false;
        catchUpPollDoneRef.current = false;

        const clearScheduled = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };

        const getRemainingWindowMs = () => {
            const elapsed = Date.now() - pollStartRef.current;
            return Math.max(0, CUTOFF - elapsed);
        };

        const isExpired = () => {
            return getRemainingWindowMs() <= 0;
        };

        const scheduleNext = () => {
            clearScheduled();

            if (pollIndexRef.current >= POLL_SCHEDULE_MS.length) {
                return;
            }

            if (isExpired()) {
                return;
            }

            const plannedDelay = POLL_SCHEDULE_MS[pollIndexRef.current];
            const delay = Math.min(plannedDelay, getRemainingWindowMs());
            timeoutRef.current = setTimeout(tick, delay);
        };

        const doPollNow = ({ force = false } = {}) => {
            if (pollIndexRef.current >= POLL_SCHEDULE_MS.length) {
                clearScheduled();
                return;
            }
            // Allow a one-time catch-up poll when returning to a visible tab after cutoff.
            if (isExpired() && !force) {
                clearScheduled();
                return;
            }

            if (force && catchUpPollDoneRef.current) {
                return;
            }

            if (force) {
                catchUpPollDoneRef.current = true;
            }

            missedWhileHiddenRef.current = false;
            revalidate();

            pollIndexRef.current += 1;
            scheduleNext();
        };

        const tick = () => {
            if (document.visibilityState === 'visible') {
                doPollNow();
            } else {
                missedWhileHiddenRef.current = true;
                scheduleNext();
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible' && missedWhileHiddenRef.current) {
                // Run one immediate poll on return so hidden-time misses still revalidate once.
                doPollNow({ force: true });
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        scheduleNext();

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            clearScheduled();
        };
    }, [needsPolling, revalidate]);
};
